// DocBook Communication Engine — private 1:1 call reference client.
// SFU-routed via mediasoup-client (Milestone "ADR-016 private-call SFU
// migration"), NOT a raw RTCPeerConnection anymore - see
// src/app/callOrchestrator.ts's header comment for why: ADR-016 point 6
// requires private-call media to be server-routed so the compliance/safety
// retention tap (docker/sfu/src/recorder.ts) has something to capture. This
// is the one place ADR-005's "private calls stay zero-dependency" claim no
// longer holds - it now shares mediasoup-client with client/web-src/groupCall.js,
// the same already-approved exception (ADR-005's client-dependency decision),
// just extended to cover this client too. Bundled by tools/build-client-bundle.mjs
// into client/web/app.js (this file is the source; that one is a generated bundle).
//
// This is a manual-testing tool, not the real DocBook product UI.
import { Device } from "mediasoup-client";

const PROTOCOL_VERSION = "1.0";

let ws = null;
let mySubjectId = null;
let seq = 0;
let currentSessionId = null;
let currentRoomId = null;
let peerSubjectId = null;
let device = null;
let sendTransport = null;
let recvTransport = null;
let localStream = null;
let screenStream = null;
let screenProducer = null;
let cameraAudioProducer = null;
let cameraVideoProducer = null;
const pendingRequests = new Map(); // requestId (our own sent messageId) -> {resolve, reject}
const consumersByProducerId = new Map(); // producerId -> {consumerId, track, source}
// SFU_NEW_PRODUCER can arrive before recvTransport finishes setup - queue
// rather than silently drop, and drain once ready (same pattern as groupCall.js).
let recvTransportReady = false;
const pendingRemoteProducers = [];

// Reconnection state (mirrors src/sdk/communicationEngineClient.ts's real
// design exactly - exponential backoff with full jitter, bounded attempts,
// never lockstep-retrying every disconnected client against the gateway's
// own per-IP connection-rate limit from Milestone 12). This reference
// client had none at all until this pass - any signaling drop just showed
// "Disconnected" forever with no recovery, a real, honest gap surfaced
// when asked directly "is anything still missing".
const RECONNECT_BASE_DELAY_MS = 500;
const RECONNECT_MAX_DELAY_MS = 30000;
const RECONNECT_MAX_ATTEMPTS = 10;
let reconnectAttempt = 0;
let reconnectTimer = null;
let lastAssertion = null;

const $ = (id) => document.getElementById(id);

function log(line) {
  const el = $("log");
  el.textContent += line + "\n";
  el.scrollTop = el.scrollHeight;
}

function resetSequence() {
  seq = 0;
}

function buildEnvelope(type, extra = {}) {
  return {
    protocolVersion: PROTOCOL_VERSION,
    type,
    messageId: crypto.randomUUID(),
    sessionId: currentSessionId,
    roomId: currentRoomId,
    senderId: mySubjectId,
    targetId: null,
    sequenceNumber: seq++,
    idempotencyKey: crypto.randomUUID(),
    timestamp: Date.now(),
    payload: {},
    ...extra
  };
}

function send(envelope) {
  ws.send(JSON.stringify(envelope));
  log(`-> ${envelope.type}`);
}

/** Sends a request and resolves when the correlated response (payload.requestId matching this message's id) arrives. */
function sendRequest(type, payload) {
  const envelope = buildEnvelope(type, { payload });
  return new Promise((resolve, reject) => {
    pendingRequests.set(envelope.messageId, { resolve, reject });
    send(envelope);
  });
}

async function populateDeviceLists() {
  let devices;
  try {
    devices = await navigator.mediaDevices.enumerateDevices();
  } catch {
    return;
  }
  const cameras = devices.filter((d) => d.kind === "videoinput");
  const mics = devices.filter((d) => d.kind === "audioinput");

  for (const [select, list] of [
    [$("cameraSelect"), cameras],
    [$("cameraSwitchSelect"), cameras],
    [$("micSelect"), mics]
  ]) {
    const previousValue = select.value;
    select.innerHTML = '<option value="">Default</option>';
    for (const d of list) {
      const opt = document.createElement("option");
      opt.value = d.deviceId;
      opt.textContent = d.label || `${d.kind} (${d.deviceId.slice(0, 8)})`;
      select.appendChild(opt);
    }
    select.value = previousValue;
  }
}

async function connect() {
  const subjectId = $("subjectId").value.trim();
  const displayName = $("displayName").value.trim() || subjectId;
  if (!subjectId) {
    alert("subject id is required");
    return;
  }
  mySubjectId = subjectId;

  const res = await fetch(`/dev/identity?subjectId=${encodeURIComponent(subjectId)}&displayName=${encodeURIComponent(displayName)}`);
  ({ assertion: lastAssertion } = await res.json());

  openSocket();
}

/** Builds and wires a fresh WebSocket - shared by the initial connect() and every reconnect attempt, so reconnection is a real new connection, not a simulation. */
function openSocket() {
  ws = new WebSocket(`ws://${location.host}/?assertion=${encodeURIComponent(lastAssertion)}`);

  ws.addEventListener("open", () => {
    const wasReconnecting = reconnectAttempt > 0;
    reconnectAttempt = 0;
    $("connectStatus").textContent = `Connected as ${mySubjectId}`;
    $("call-panel").hidden = false;
    log(wasReconnecting ? "reconnected" : `connected as ${mySubjectId}`);
    // A resumed connection has no room/call-state resync of its own here
    // (this reference UI predates ROOM_STATE_SYNC and has no notion of
    // "resume the call I was in" - a real integration uses the SDK's own
    // syncRoomState/session:resynced instead, see docs/sdk/01-integration-guide.md#reconnection).
    // Signaling capability is what's genuinely restored; anything further
    // is out of scope for this manual-testing tool.
  });
  ws.addEventListener("message", (event) => {
    void handleEnvelope(JSON.parse(event.data));
  });
  ws.addEventListener("close", (event) => {
    $("connectStatus").textContent = "Disconnected";
    log(`disconnected (code ${event.code})`);
    scheduleReconnect();
  });
  ws.addEventListener("error", () => log("websocket error"));
}

function scheduleReconnect() {
  if (reconnectAttempt >= RECONNECT_MAX_ATTEMPTS) {
    log(`reconnect: gave up after ${reconnectAttempt} attempts`);
    $("connectStatus").textContent = "Disconnected (gave up reconnecting)";
    return;
  }
  reconnectAttempt += 1;
  const capped = Math.min(RECONNECT_MAX_DELAY_MS, RECONNECT_BASE_DELAY_MS * 2 ** (reconnectAttempt - 1));
  const delayMs = Math.random() * capped;
  $("connectStatus").textContent = `Reconnecting (attempt ${reconnectAttempt})...`;
  log(`reconnect: attempt ${reconnectAttempt} in ${Math.round(delayMs)}ms`);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    openSocket();
  }, delayMs);
}

async function handleEnvelope(envelope) {
  const requestId = envelope.payload && envelope.payload.requestId;
  if (requestId && pendingRequests.has(requestId)) {
    const { resolve } = pendingRequests.get(requestId);
    pendingRequests.delete(requestId);
    log(`<- ${envelope.type}`);
    resolve(envelope.payload);
    return;
  }
  const inResponseTo = envelope.type === "ERROR" && envelope.payload && envelope.payload.inResponseTo;
  if (inResponseTo && pendingRequests.has(inResponseTo)) {
    const { reject } = pendingRequests.get(inResponseTo);
    pendingRequests.delete(inResponseTo);
    log(`<- ERROR ${envelope.payload.reasonCode}: ${envelope.payload.message}`);
    reject(new Error(`${envelope.payload.reasonCode}: ${envelope.payload.message}`));
    return;
  }

  log(`<- ${envelope.type}`);

  switch (envelope.type) {
    case "CALL_INVITE": {
      resetSequence();
      currentSessionId = envelope.sessionId;
      peerSubjectId = envelope.payload.callerSubjectId;
      $("incoming-panel").hidden = false;
      $("incomingFrom").textContent = `Incoming call from ${peerSubjectId}`;
      break;
    }
    case "CALL_RINGING":
      $("callStatus").textContent = "Ringing...";
      break;
    case "CALL_ACCEPT": {
      currentRoomId = envelope.payload.roomId;
      peerSubjectId = envelope.payload.peerSubjectId;
      $("incoming-panel").hidden = true;
      $("in-call-panel").hidden = false;
      $("callStatus").textContent = "";
      await startSfuMedia(envelope.payload.rtpCapabilities);
      break;
    }
    case "CALL_REJECT":
      log(`${peerSubjectId} rejected the call`);
      resetCallUi();
      break;
    case "CALL_BUSY":
      log(`${peerSubjectId ?? "callee"} is busy`);
      resetCallUi();
      break;
    case "CALL_CANCEL":
      log("call cancelled");
      resetCallUi();
      break;
    case "CALL_TIMEOUT":
      log("call timed out (no answer)");
      resetCallUi();
      break;
    case "SFU_NEW_PRODUCER": {
      const { participantId, producerId, kind, source } = envelope.payload;
      if (recvTransportReady) {
        void consumeRemoteProducer(participantId, producerId, kind, source);
      } else {
        pendingRemoteProducers.push({ participantId, producerId, kind, source });
      }
      break;
    }
    case "SFU_PRODUCER_CLOSED":
      if (envelope.payload.producerId) {
        removeRemoteProducer(envelope.payload.producerId);
      }
      break;
    case "SESSION_END":
      log("other party hung up");
      endCall();
      break;
    case "ERROR":
      log(`ERROR ${envelope.payload.reasonCode}: ${envelope.payload.message}`);
      break;
    default:
      break;
  }
}

async function getLocalStream() {
  const cameraId = $("cameraSelect").value;
  const micId = $("micSelect").value;
  const constraints = {
    audio: micId ? { deviceId: { exact: micId } } : true,
    video: cameraId ? { deviceId: { exact: cameraId } } : true
  };
  const stream = await navigator.mediaDevices.getUserMedia(constraints);
  // "Start with camera on" only controls the initial enabled state, not
  // whether we capture video at all - capturing it either way means camera
  // toggle later is a local track.enabled flip, no renegotiation needed.
  const wantVideoOn = $("videoEnabled").checked;
  for (const track of stream.getVideoTracks()) track.enabled = wantVideoOn;
  return stream;
}

/**
 * Media-layer recovery (security/resilience hardening pass): a transport
 * whose connectionState goes "disconnected" (a network blip - often
 * self-heals) or "failed" (it won't heal on its own) requests a real ICE
 * restart from the SFU and applies the fresh credentials via
 * mediasoup-client's own `transport.restartIce()` - this rotates ICE
 * without tearing down DTLS/SRTP or any existing producers/consumers on
 * the transport. `ICE_RESTART_SUCCESS` has been reserved in the call state
 * machine since Milestone 1; this is what actually produces that event.
 * "disconnected" gets a short grace period before restarting (a real
 * network blip often recovers on its own within a couple seconds without
 * needing a restart at all) - "failed" restarts immediately, since mediasoup
 * won't recover that state by itself.
 */
function attachIceRestartRecovery(transport, label) {
  let restarting = false;
  let disconnectedTimer = null;

  async function restart() {
    if (restarting) return;
    restarting = true;
    log(`${label} transport recovery: requesting ICE restart`);
    try {
      const { iceParameters } = await sendRequest("SFU_RESTART_ICE", { transportId: transport.id });
      await transport.restartIce({ iceParameters });
      log(`${label} transport recovery: ICE restart applied`);
    } catch (err) {
      log(`${label} transport recovery: ICE restart failed - ${err.message}`);
    } finally {
      restarting = false;
    }
  }

  transport.on("connectionstatechange", (state) => {
    if (disconnectedTimer) {
      clearTimeout(disconnectedTimer);
      disconnectedTimer = null;
    }
    if (state === "failed") {
      void restart();
    } else if (state === "disconnected") {
      disconnectedTimer = setTimeout(() => void restart(), 3000);
    }
  });

  // Test-only hook (tools/verify-ice-restart.mjs): reliably forcing a *real*
  // network-level ICE failure in an automated headless-browser test isn't
  // reproducible on demand, so the verify script instead calls this to
  // manually invoke the exact same restart() path a real "failed"/
  // "disconnected" transition would - genuinely exercising the recovery
  // mechanism (real SFU-minted ICE parameters, real transport.restartIce()
  // call, real media still flowing after) without needing to simulate an
  // actual outage.
  window[`__testRestartIce_${label}`] = restart;
}

async function startSfuMedia(rtpCapabilities) {
  device = new Device();
  await device.load({ routerRtpCapabilities: rtpCapabilities });

  const sendInfo = await sendRequest("SFU_CREATE_TRANSPORT", {});
  sendTransport = device.createSendTransport({
    id: sendInfo.transportId,
    iceParameters: sendInfo.iceParameters,
    iceCandidates: sendInfo.iceCandidates,
    dtlsParameters: sendInfo.dtlsParameters
  });
  sendTransport.on("connect", ({ dtlsParameters }, callback, errback) => {
    sendRequest("SFU_CONNECT_TRANSPORT", { transportId: sendTransport.id, dtlsParameters }).then(() => callback()).catch(errback);
  });
  sendTransport.on("produce", ({ kind, rtpParameters, appData }, callback, errback) => {
    sendRequest("SFU_PRODUCE", { transportId: sendTransport.id, kind, rtpParameters, appData })
      .then(({ producerId }) => callback({ id: producerId }))
      .catch(errback);
  });
  sendTransport.on("connectionstatechange", (state) => {
    $("peerConnectionState").textContent = `Connection: ${state}`;
    log(`sendTransport connectionState = ${state}`);
  });
  attachIceRestartRecovery(sendTransport, "send");

  const recvInfo = await sendRequest("SFU_CREATE_TRANSPORT", {});
  recvTransport = device.createRecvTransport({
    id: recvInfo.transportId,
    iceParameters: recvInfo.iceParameters,
    iceCandidates: recvInfo.iceCandidates,
    dtlsParameters: recvInfo.dtlsParameters
  });
  recvTransport.on("connect", ({ dtlsParameters }, callback, errback) => {
    sendRequest("SFU_CONNECT_TRANSPORT", { transportId: recvTransport.id, dtlsParameters }).then(() => callback()).catch(errback);
  });
  attachIceRestartRecovery(recvTransport, "recv");

  recvTransportReady = true;
  const queued = pendingRemoteProducers.splice(0, pendingRemoteProducers.length);
  for (const { participantId, producerId, kind, source } of queued) {
    void consumeRemoteProducer(participantId, producerId, kind, source);
  }

  localStream = await getLocalStream();
  $("localVideo").srcObject = localStream;
  $("cameraToggleBtn").textContent = $("videoEnabled").checked ? "Camera off" : "Camera on";
  for (const track of localStream.getTracks()) {
    const producer = await sendTransport.produce({ track, appData: { source: "camera" } });
    if (track.kind === "audio") cameraAudioProducer = producer;
    else cameraVideoProducer = producer;
  }

  // Codec choice is driven by the router's own rtpCapabilities ordering
  // (VP9 > VP8 > H.264, see docker/sfu/src/roomRegistry.ts) - mediasoup-client
  // negotiates against that automatically, unlike the old raw
  // RTCPeerConnection path which needed an explicit setCodecPreferences()
  // call. This just reports what was actually negotiated.
  if (cameraVideoProducer) {
    const negotiatedCodec = cameraVideoProducer.rtpParameters.codecs[0]?.mimeType ?? "unknown";
    $("codecInfo").textContent = `Video codec: ${negotiatedCodec}`;
    log(`negotiated video codec: ${negotiatedCodec}`);
  }

  await populateDeviceLists(); // labels are only populated once permission has been granted
}

async function consumeRemoteProducer(participantId, producerId, kind, source) {
  if (!device || !recvTransport) return;
  const { consumerId, kind: consumerKind, rtpParameters } = await sendRequest("SFU_CONSUME", {
    transportId: recvTransport.id,
    producerId,
    rtpCapabilities: device.rtpCapabilities
  });
  const consumer = await recvTransport.consume({ id: consumerId, producerId, kind: consumerKind, rtpParameters });
  await sendRequest("SFU_RESUME_CONSUMER", { consumerId });
  consumersByProducerId.set(producerId, { consumerId, track: consumer.track, source: source ?? "camera" });
  attachRemoteTrack(consumer.track, source);
}

function attachRemoteTrack(track, source) {
  const videoEl = source === "screen" ? $("remoteScreenVideo") : $("remoteVideo");
  let stream = videoEl.srcObject;
  if (!stream) {
    stream = new MediaStream();
    videoEl.srcObject = stream;
  }
  stream.addTrack(track);
}

/** Removes just one remote producer's track (e.g. the other party stopped screen sharing) - the camera/mic tracks only go away via SESSION_END/endCall. */
function removeRemoteProducer(producerId) {
  const info = consumersByProducerId.get(producerId);
  if (!info) return;
  consumersByProducerId.delete(producerId);
  const videoEl = info.source === "screen" ? $("remoteScreenVideo") : $("remoteVideo");
  if (videoEl.srcObject) {
    videoEl.srcObject.removeTrack(info.track);
    if (videoEl.srcObject.getTracks().length === 0) videoEl.srcObject = null;
  }
}

/**
 * Screen sharing (Milestone 7, now SFU-routed): a separate producer
 * alongside the camera producers, never a replacement. Simpler than the old
 * RTCPeerConnection.addTrack + renegotiation path (no glare concern - each
 * producer is independent), and gets the same real-time delivery as camera
 * tracks. contentHint = 'detail' matches the documented encoding profile -
 * see docs/scalability/03-codec-and-compatibility.md#screen-share-encoding-profile.
 * No host/permission concept for a private call - either party may always share.
 */
async function startScreenShare() {
  if (!sendTransport || screenProducer) return;
  try {
    screenStream = await navigator.mediaDevices.getDisplayMedia({ video: true });
  } catch (err) {
    log(`getDisplayMedia failed or was cancelled: ${err}`);
    return;
  }
  const track = screenStream.getVideoTracks()[0];
  track.contentHint = "detail";
  track.addEventListener("ended", () => void stopScreenShare()); // browser's native "Stop sharing" control

  try {
    screenProducer = await sendTransport.produce({ track, appData: { source: "screen" } });
  } catch (err) {
    log(`screen share rejected: ${err}`);
    for (const t of screenStream.getTracks()) t.stop();
    screenStream = null;
    return;
  }

  $("localScreenVideo").srcObject = screenStream;
  $("screenShareBtn").hidden = true;
  $("stopShareBtn").hidden = false;
}

async function stopScreenShare() {
  if (!screenProducer) return;
  const producerId = screenProducer.id;
  screenProducer.close();
  screenProducer = null;
  if (screenStream) {
    for (const t of screenStream.getTracks()) t.stop();
    screenStream = null;
  }
  $("localScreenVideo").srcObject = null;
  try {
    await sendRequest("SFU_CLOSE_PRODUCER", { producerId });
  } catch (err) {
    log(`SFU_CLOSE_PRODUCER failed: ${err}`);
  }
  $("screenShareBtn").hidden = false;
  $("stopShareBtn").hidden = true;
}

function resetCallUi() {
  $("incoming-panel").hidden = true;
  $("in-call-panel").hidden = true;
  $("callStatus").textContent = "";
  $("codecInfo").textContent = "";
  $("localVideo").srcObject = null;
  $("remoteVideo").srcObject = null;
  $("localScreenVideo").srcObject = null;
  $("remoteScreenVideo").srcObject = null;
  $("screenShareBtn").hidden = false;
  $("stopShareBtn").hidden = true;
  currentSessionId = null;
  currentRoomId = null;
  peerSubjectId = null;
  consumersByProducerId.clear();
  recvTransportReady = false;
  pendingRemoteProducers.length = 0;
}

function endCall() {
  if (screenProducer) {
    screenProducer.close();
    screenProducer = null;
  }
  if (screenStream) {
    for (const track of screenStream.getTracks()) track.stop();
    screenStream = null;
  }
  cameraAudioProducer = null;
  cameraVideoProducer = null;
  if (sendTransport) {
    sendTransport.close();
    sendTransport = null;
  }
  if (recvTransport) {
    recvTransport.close();
    recvTransport = null;
  }
  device = null;
  if (localStream) {
    for (const track of localStream.getTracks()) track.stop();
    localStream = null;
  }
  resetCallUi();
}

$("connectBtn").addEventListener("click", () => void connect());

navigator.mediaDevices?.addEventListener?.("devicechange", () => void populateDeviceLists());
void populateDeviceLists(); // best-effort on load; labels fill in once a call has requested permission

$("callBtn").addEventListener("click", () => {
  const calleeId = $("calleeId").value.trim();
  if (!calleeId) return;
  resetSequence();
  currentSessionId = crypto.randomUUID();
  send(buildEnvelope("CALL_INVITE", { payload: { calleeSubjectId: calleeId } }));
  $("callStatus").textContent = `Calling ${calleeId}...`;
});

$("acceptBtn").addEventListener("click", () => send(buildEnvelope("CALL_ACCEPT")));

$("rejectBtn").addEventListener("click", () => {
  send(buildEnvelope("CALL_REJECT"));
  resetCallUi();
});

$("hangupBtn").addEventListener("click", () => {
  send(buildEnvelope("SESSION_END"));
  endCall();
});

$("screenShareBtn").addEventListener("click", () => void startScreenShare());
$("stopShareBtn").addEventListener("click", () => void stopScreenShare());

$("muteBtn").addEventListener("click", () => {
  if (!localStream) return;
  const track = localStream.getAudioTracks()[0];
  if (!track) return;
  track.enabled = !track.enabled;
  $("muteBtn").textContent = track.enabled ? "Mute mic" : "Unmute mic";
});

$("cameraToggleBtn").addEventListener("click", () => {
  if (!localStream) return;
  const track = localStream.getVideoTracks()[0];
  if (!track) return;
  track.enabled = !track.enabled;
  $("cameraToggleBtn").textContent = track.enabled ? "Camera off" : "Camera on";
});

$("cameraSwitchSelect").addEventListener("change", () => {
  void switchCamera($("cameraSwitchSelect").value);
});

async function switchCamera(deviceId) {
  if (!cameraVideoProducer || !localStream) return;
  const newStream = await navigator.mediaDevices.getUserMedia({
    video: deviceId ? { deviceId: { exact: deviceId } } : true
  });
  const newTrack = newStream.getVideoTracks()[0];
  const oldTrack = localStream.getVideoTracks()[0];

  await cameraVideoProducer.replaceTrack({ track: newTrack });
  if (oldTrack) {
    oldTrack.stop();
    localStream.removeTrack(oldTrack);
  }
  localStream.addTrack(newTrack);
  $("localVideo").srcObject = localStream;
  log(`switched camera to ${newTrack.label || deviceId || "default"}`);
}
